import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider.jsx";
import { api } from "../api/client.js";
import MeetingDocument from "./MeetingDocument.jsx";
import { publicView, publicLoeRows } from "./publicView.js";
import { computeLoe, computeTiles } from "./docData.js";
import { computeCrm } from "./crmBlock.js";
import { loadCrmData } from "./useCrmData.js";
import { localToday } from "./period.js";
import { paper, sans } from "./docTheme.js";

// Presenter pop-out (spec §3.4): opened by MeetingView as `/#present=<meetingId>`; src/main.jsx
// renders this INSTEAD of the app, so the shared window has no sidebar, nav, or Ops Hub links.
// It renders publicView(meeting) only — no sources, review flags, or SharePoint links.
export function readPresentHash(hash = typeof window !== "undefined" ? window.location.hash : "") {
  const m = /present=([^&]+)/.exec(hash || "");
  return m ? decodeURIComponent(m[1]) : null;
}

const SIGN_IN = "Please sign in again in the Ops Hub, then reopen this window.";
const STALE = "Couldn’t refresh — showing the last loaded version.";
const isUnauthorized = (e) => /unauthori[sz]ed|\b401\b/i.test(String(e?.message));
const noop = async () => ({ ok: true });
const btn = { padding: "10px 18px", borderRadius: 8, border: `1px solid ${paper.line}`, background: paper.card, color: paper.inkSoft, fontFamily: sans, fontSize: 15, cursor: "pointer" };

function Plain({ children }) {
  return <div style={{ minHeight: "100vh", background: paper.bg, color: paper.ink, fontFamily: sans, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontSize: 18, gap: 12 }}>{children}</div>;
}

function AgendaBar({ items, onToggle }) {
  const done = items.filter((i) => i.checked).length;
  return (
    <div role="region" aria-label="Agenda" style={{ position: "sticky", top: 0, zIndex: 10, background: paper.ink, color: paper.bg, padding: "12px 24px", display: "flex", alignItems: "center", gap: 16, fontFamily: sans, fontSize: 14, flexWrap: "wrap" }}>
      <span aria-live="polite" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>Agenda {done} / {items.length}</span>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
        {items.map((i) => (
          <label key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 6, background: "rgba(255,255,255,0.08)", color: i.checked ? "#9aa3b5" : paper.bg, textDecoration: i.checked ? "line-through" : "none", cursor: "pointer" }}>
            <input type="checkbox" checked={i.checked} aria-label={`${i.headline} (agenda bar)`} onChange={(e) => onToggle(i.id, e.target.checked)} style={{ width: 16, height: 16, accentColor: paper.accent }} />
            <span aria-hidden="true">{i.headline}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Presenter({ meetingId }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | signedOut | error | ready
  const [data, setData] = useState(null); // { meeting, clientName, users, objectives, tasks, proposals }
  const [ending, setEnding] = useState(false);
  // One quiet line under the document: { kind, msg }. kind lets each source clear only its own
  // message ("stale" = a background reload failed, "agenda" = a check-off failed, "end" = End meeting).
  const [notice, setNoticeState] = useState(null);
  const setNotice = useCallback((kind, msg) => setNoticeState({ kind, msg }), []);
  const clearNotice = useCallback((kind) => setNoticeState((n) => (n?.kind === kind ? null : n)), []);
  // Whether a document is on screen. A reload that fails then (focus/Refresh while presenting)
  // must not swap the client-facing screen for an error page — it keeps the last good version.
  const hasDataRef = useRef(false);

  // A reload (initial load, Refresh, or focus) can straddle a check-off/End-meeting write that
  // finishes first: without this, the reload's now-stale response would clobber the write's
  // result when it resolves later. loadTokenRef makes only the most recently STARTED load apply
  // its own result; writeVersionRef additionally discards a load whose response arrives after a
  // write has already completed while that load was in flight, since the write is authoritative.
  const loadTokenRef = useRef(0);
  const writeVersionRef = useRef(0);

  const load = useCallback(async () => {
    const token = ++loadTokenRef.current;
    const versionAtStart = writeVersionRef.current;
    try {
      const meeting = await api.meetings.get(meetingId);
      const [client, users, objectives, tasks, proposals, crmData] = await Promise.all([
        api.clients.get(meeting.clientId), api.users.list(),
        api.objectives.list({ clientId: meeting.clientId }), api.tasks.list({ clientId: meeting.clientId }), api.proposals.list(),
        loadCrmData(meeting.clientId),
      ]);
      if (token !== loadTokenRef.current || writeVersionRef.current !== versionAtStart) return;
      setData({
        meeting, clientName: client?.name || "", users: users || [],
        objectives: objectives?.items || [], tasks: tasks?.items || [],
        proposals: (proposals || []).filter((p) => p.clientId === meeting.clientId),
        crmData,
      });
      hasDataRef.current = true;
      setStatus("ready");
      clearNotice("stale");
    } catch (e) {
      if (token !== loadTokenRef.current || writeVersionRef.current !== versionAtStart) return;
      if (isUnauthorized(e)) setStatus("signedOut");
      else if (hasDataRef.current) setNotice("stale", STALE);
      else setStatus("error");
    }
  }, [meetingId, setNotice, clearNotice]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setStatus("signedOut"); return; }
    load();
  }, [authLoading, isAuthenticated, load]);

  // A colleague may edit the draft mid-meeting: pick it up whenever this window regains focus.
  useEffect(() => {
    if (status !== "ready") return undefined;
    const onFocus = () => { load(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [status, load]);

  useEffect(() => {
    if (data) document.title = `${data.clientName} · ${data.meeting.type === "kickoff" ? "Kickoff" : `Meeting ${data.meeting.number}`}`;
  }, [data]);

  const view = useMemo(() => (data ? publicView(data.meeting) : null), [data]);
  // Objective and task names come from raw project data, not the meeting doc: strip them too.
  const loeRows = useMemo(() => (view ? publicLoeRows(computeLoe(data.objectives, data.tasks, { projectId: view.projectId, periodStart: view.periodStart, periodEnd: view.periodEnd, hiddenObjectiveIds: view.loe.hiddenObjectiveIds })) : []), [view, data]);
  const tiles = useMemo(() => (view ? computeTiles({ tasks: data.tasks, objectives: data.objectives, proposals: data.proposals, sections: view.sections, periodStart: view.periodStart, periodEnd: view.periodEnd, today: localToday(), hiddenTiles: view.summary.hiddenTiles }) : []), [view, data]);
  const crm = useMemo(() => (view && data.crmData ? computeCrm(data.crmData.tracks, data.crmData.contacts, { periodStart: view.periodStart, periodEnd: view.periodEnd, hiddenEventIds: view.crm.hiddenEventIds }) : null), [view, data]);

  if (status === "signedOut") return <Plain>{SIGN_IN}</Plain>;
  if (status === "error") return <Plain><span>This meeting couldn’t be loaded.</span><button type="button" style={btn} onClick={load}>Try again</button></Plain>;
  if (!view) return <Plain>Loading…</Plain>;

  const toggle = async (itemId, checked) => {
    const res = await api.meetings.setAgenda(view.id, itemId, checked);
    // writeRequest() never throws — a session that expired mid-meeting comes back as a plain
    // { ok: false, status: 401 } here, not a thrown Unauthorized like the load path, so it needs
    // its own check to land on the same sign-in-only screen instead of a generic save error.
    if (res.status === 401) { setStatus("signedOut"); return { ok: false }; }
    if (res.ok) { writeVersionRef.current += 1; setData((d) => ({ ...d, meeting: res.data })); return { ok: true }; }
    return { ok: false, error: res.data?.error || "Couldn’t save that check-off." };
  };
  // The in-document agenda shows its own inline error; the sticky bar reports through the notice.
  const barToggle = async (itemId, checked) => {
    const r = await toggle(itemId, checked);
    if (r.ok) clearNotice("agenda");
    else if (r.error) setNotice("agenda", r.error);
  };
  const endMeeting = async () => {
    setEnding(true);
    const res = await api.meetings.setStatus(view.id, "presented");
    setEnding(false);
    if (res.status === 401) { setStatus("signedOut"); return; }
    if (!res.ok) { setNotice("end", res.data?.error || "Couldn’t mark the meeting as presented."); return; }
    writeVersionRef.current += 1;
    setData((d) => ({ ...d, meeting: res.data }));
    window.close();
    // Browsers refuse to close windows they didn't open by script (e.g. a pasted URL).
    setNotice("end", "Meeting marked as presented — you can close this window.");
  };
  const agenda = view.sections.find((s) => s.kind === "agenda");

  return (
    <div style={{ minHeight: "100vh", background: paper.bg }}>
      {agenda && agenda.items.length > 0 && <AgendaBar items={agenda.items} onToggle={barToggle} />}
      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 64px" }}>
        <MeetingDocument meeting={view} clientName={data.clientName} users={data.users} loeRows={loeRows} tiles={tiles} crm={crm} editable={false}
          onSaveSection={noop} onAddSection={noop} onRemoveSection={noop} onMoveSection={noop} onSaveBlock={noop} onToggleAgenda={toggle} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 28, fontFamily: sans }}>
          {notice && <div role="status" style={{ flex: 1, fontSize: 15, color: paper.inkSoft }}>{notice.msg}</div>}
          {!notice && <span style={{ flex: 1 }} />}
          <button type="button" style={btn} onClick={load}>Refresh</button>
          <button type="button" style={{ ...btn, background: paper.accent, borderColor: paper.accent, color: "#fff", fontWeight: 600 }} onClick={endMeeting} disabled={ending}>
            {ending ? "Ending…" : "End meeting"}
          </button>
        </div>
      </main>
    </div>
  );
}
